/**
 * AddLiveMessageUseCase
 *
 * Persists an ALREADY-BUILT live chat message via
 * `LiveStreamingService.addLiveMessage` and reports the write outcome — the
 * live-session WRITE the Task-7 hook (`useLiveStreamingChat.sendText`) awaits
 * after its optimistic insert. This mirrors the wall's
 * `AddCommunityMessageUseCase`: the optimistic entity is built in
 * `CommunityChatMessageRemapper.fromLiveInput` (Task 3, the canonical live-input
 * mapper) and inserted at index 0 (input cleared, scrolled to top) by the hook
 * BEFORE this await — so this use case no longer builds the entity, it receives
 * it and only persists + reports.
 *
 * Flutter source: lib/presentation/screens/community/live_streaming_screen.dart:760-821
 *   (`addLiveMessage`).
 *   - `live_streaming_screen.dart:800` — optimistic insert at index 0 (BEFORE the
 *     await), the hook's concern.
 *   - `live_streaming_screen.dart:814` —
 *     `success = await StreamingRepository().addLiveMessage(message)`; this use
 *     case is the web analog of that single await.
 *
 * Parity with the wall write (`community_chat_mssage.dart:18`, `bool success =
 * true`): the live send never mutates the passed entity's `success` on a failed
 * write — this use case only reports the write boolean and never touches the
 * entity. The behavioural divergence of removing a failed text message from the
 * list by id lives in the Task-7 hook, not here.
 *
 * Pure-domain contract (conventions.md § Clean Architecture flow):
 *   - Calls `LiveStreamingService.addLiveMessage`; does NOT touch
 *     `firestoreService` directly and does NOT dispatch to Redux.
 */

import { LiveStreamingService } from '@/domain/services/liveStreaming/liveStreamingService';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';

export interface AddLiveMessageResult {
  /** `true` on a successful Firestore write, `false` otherwise. */
  success: boolean;
}

export class AddLiveMessageUseCase {
  private liveStreamingService: LiveStreamingService;

  constructor(liveStreamingService: LiveStreamingService = LiveStreamingService.getInstance()) {
    this.liveStreamingService = liveStreamingService;
  }

  /**
   * Persist an already-built live message via `LiveStreamingService.addLiveMessage`
   * — the Firestore write only. The Task-7 hook builds the entity via
   * `CommunityChatMessageRemapper.fromLiveInput`, does the optimistic insert, then
   * awaits this (`StreamingRepository().addLiveMessage(message)`,
   * live_streaming_screen.dart:814).
   *
   * @returns `{ success: true }` on a successful write, `{ success: false }`
   *   otherwise. The passed entity is never mutated — see the class JSDoc.
   */
  async execute(message: CommunityChatMessageEntity): Promise<AddLiveMessageResult> {
    const success = await this.liveStreamingService.addLiveMessage(message);
    return { success };
  }
}

export const addLiveMessageUseCase = new AddLiveMessageUseCase();
